import { DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  RdsButtonComponent,
  RdsDatepickerComponent,
  RdsDatepickerInputDirective,
  RdsDatepickerToggleComponent,
  RdsEmptyStateComponent,
  RdsEmptyStateImageComponent,
  RdsEmptyStateSubtitleDirective,
  RdsEmptyStateTitleDirective,
  RdsFormFieldComponent,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsInputDirective,
  RdsProgressSpinnerComponent,
  RdsSwitchComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import { differenceInCalendarDays, eachDayOfInterval, sub } from 'date-fns';
import { add } from 'date-fns/add';
import { endOfWeek } from 'date-fns/endOfWeek';
import { startOfWeek } from 'date-fns/startOfWeek';

import { dateClass } from '../../../core/adapters/custom-date.adapter';
import {
  CalendarCellClickEvent,
  CalendarEvent,
  CalendarEventClickEvent,
  CalendarRoomRow,
} from '../../model/calendar.model';

/** Fixed reference date used to convert event dates into absolute day indices. */
const DAY_EPOCH = new Date(2020, 0, 1);

/** A rendered event card within a day cell, with its visible (clipped) span. */
interface CalendarSlot {
  event: CalendarEvent;
  /** Number of day columns the card visually spans within the current week. */
  span: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    ReactiveFormsModule,
    RdsButtonComponent,
    RdsIconComponent,
    RdsDatepickerInputDirective,
    RdsDatepickerComponent,
    RdsDatepickerToggleComponent,
    RdsProgressSpinnerComponent,
    RdsFormFieldComponent,
    RdsInputDirective,
    RdsIconButtonComponent,
    RdsSwitchComponent,
    RdsTooltipDirective,
    RdsEmptyStateComponent,
    RdsEmptyStateImageComponent,
    RdsEmptyStateTitleDirective,
    RdsEmptyStateSubtitleDirective,
  ],
  selector: 'bsh-week-calendar',
  styleUrl: './week-calendar.component.scss',
  templateUrl: './week-calendar.component.html',
})
export class WeekCalendarComponent implements OnInit {
  /** Room rows with their events to display */
  readonly rooms = input.required<CalendarRoomRow[]>();

  /** Whether the calendar data is loading */
  readonly isLoading = input<boolean>(false);

  /** Sort direction for rooms */
  readonly sortDirection = input<'asc' | 'desc'>('asc');

  /**
   * Whether to show the "add event" (+) button in each day cell. Off by
   * default; enable it on the user-facing calendar, keep it off for host/admin.
   */
  readonly canAddEvent = input<boolean>(false);

  /** Initial date to show (defaults to today) */
  readonly initialDate = input<Date>(new Date());

  /**
   * Whether the room name should be a clickable link to the room details. Off
   * by default; enable it on the user-facing calendar, keep it off for
   * host/admin where room details aren't the target.
   */
  readonly canOpenRoom = input<boolean>(false);

  /**
   * Whether clicking an event opens its details. On by default; disable it on
   * the user-facing calendar where events are read-only.
   */
  readonly canOpenEvent = input<boolean>(true);

  /**
   * When true, weekends are skipped: the view still shows 7 day columns, but
   * after Friday it continues with Monday and Tuesday of the following week.
   * A `model` so the in-view switch can toggle it while consumers can still set
   * the initial value (or two-way bind it).
   */
  readonly hideWeekends = model<boolean>(false);

  /** Emitted when a calendar event is clicked */
  readonly eventClick = output<CalendarEventClickEvent>();

  /** Emitted when the "add event" (+) button of a cell is clicked */
  readonly addEvent = output<CalendarCellClickEvent>();

  /** Emitted when a room name is clicked (only when `canOpenRoom` is enabled) */
  readonly roomClick = output<CalendarRoomRow>();

  /** Emitted when the visible week changes (start date of week) */
  readonly weekChange = output<{
    startDate: Date;
    endDate: Date;
    selectedDate: Date;
    userInitiated: boolean;
  }>();

  /** Emitted when sort button is clicked */
  readonly sortChange = output<'asc' | 'desc'>();

  protected readonly dateClass = dateClass;
  protected today = new Date();

  readonly #week: WritableSignal<Date[]> = signal([]);
  #selectedDay: Date = new Date();
  #initialized = false;
  #lastHideWeekends = false;

  /**
   * Cell tapped to reveal its "add event" (+) button on touch devices, where
   * hover is unavailable. Identified by room + day timestamp.
   */
  protected readonly selectedCell = signal<{
    roomDexId: string;
    time: number;
  } | null>(null);

  protected readonly dateControl = new FormControl(new Date(), {
    nonNullable: true,
  });

  protected readonly sortedRooms: Signal<CalendarRoomRow[]> = computed(() => {
    const rooms = this.rooms();
    const direction = this.sortDirection();
    return [...rooms].sort((a, b) =>
      direction === 'asc'
        ? a.roomName.localeCompare(b.roomName)
        : b.roomName.localeCompare(a.roomName)
    );
  });

  /**
   * Lane (vertical row) assigned to every event, per room. Overlapping events
   * (sharing any day) get distinct lanes so they stack instead of colliding,
   * while non-overlapping events reuse the lowest free lane and pack upwards.
   */
  readonly #roomLanes: Signal<Map<string, Map<number, number>>> = computed(
    () => {
      const map = new Map<string, Map<number, number>>();
      for (const room of this.rooms()) {
        map.set(room.roomDexId, this.#assignLanes(room.events));
      }
      return map;
    }
  );

  get week(): Signal<Date[]> {
    return this.#week.asReadonly();
  }

  constructor() {
    // React only to external `initialDate` changes. The body runs untracked so
    // that emitting (which reads `#week`) doesn't make this effect re-run on
    // every internal week change — that would emit a duplicate `weekChange`.
    effect(() => {
      const date = this.initialDate();
      untracked(() => {
        if (!this.#initialized) {
          return;
        }
        if (date.getTime() === this.#selectedDay.getTime()) {
          return;
        }
        this.#selectedDay = date;
        this.#setWeek(date);
        this.dateControl.setValue(date);
        this.#emitWeekChange();
      });
    });

    // Rebuild the visible range when the weekend visibility option changes.
    // Body runs untracked (see above); guarded on the actual value so neither
    // the initial flush nor unrelated week changes emit a spurious event.
    effect(() => {
      const hideWeekends = this.hideWeekends();
      untracked(() => {
        if (!this.#initialized) {
          return;
        }
        if (hideWeekends === this.#lastHideWeekends) {
          return;
        }
        this.#lastHideWeekends = hideWeekends;
        this.#setWeek(this.#selectedDay);
        this.#emitWeekChange();
      });
    });
  }

  ngOnInit() {
    this.#selectedDay = this.initialDate();
    this.#lastHideWeekends = this.hideWeekends();
    this.#setWeek(this.#selectedDay);
    this.dateControl.setValue(this.#selectedDay);
    this.#initialized = true;
    this.#emitWeekChange();
  }

  protected onDateChange(date: Date) {
    this.#selectedDay = date;
    this.#setWeek(date);
    this.#emitWeekChange(true);
  }

  protected subWeek() {
    const prevWeek = sub(this.#selectedDay, { weeks: 1 });
    this.dateControl.setValue(prevWeek);
    this.onDateChange(prevWeek);
  }

  protected addWeek() {
    const nextWeek = add(this.#selectedDay, { weeks: 1 });
    this.dateControl.setValue(nextWeek);
    this.onDateChange(nextWeek);
  }

  protected currentWeek() {
    this.today = new Date();
    this.#selectedDay = this.today;
    this.dateControl.setValue(this.#selectedDay);
    this.#setWeek(this.#selectedDay);
    this.#emitWeekChange(true);
  }

  /**
   * Width of an event card spanning `days` day columns, adding the gutters
   * (cell borders + padding) crossed between the spanned columns.
   */
  protected slotWidth(days: number): string {
    const gutters = days === 2 ? days * 16 : days * 32 - 32;
    return `calc(${days * 100}% + ${gutters}px)`;
  }

  /**
   * Lane-ordered slots for a single day cell. Index = lane: a `CalendarSlot`
   * means the event's card is rendered here (the day it starts, or the first
   * visible column for events that began before the week); `null` reserves the
   * lane's height for an event whose spanning card overlays this cell from an
   * earlier column, or for a lower lane, keeping rows aligned. The array only
   * reaches the deepest lane used on this day, so days without multi-day events
   * don't reserve empty space at the top.
   */
  protected daySlots(
    room: CalendarRoomRow,
    day: Date,
    dayIndex: number
  ): (CalendarSlot | null)[] {
    const lanes = this.#roomLanes().get(room.roomDexId);
    const week = this.#week();
    if (!lanes || week.length === 0) {
      return [];
    }
    const occupying = room.events.filter(event => this.#occupies(event, day));
    if (occupying.length === 0) {
      return [];
    }
    const maxLane = Math.max(
      ...occupying.map(event => lanes.get(event.bookingId) ?? 0)
    );
    const slots: (CalendarSlot | null)[] = new Array(maxLane + 1).fill(null);
    for (const event of occupying) {
      // Render the card in the first visible column the event occupies; later
      // columns it passes through reserve their lane (null). The previous
      // column owning it covers both "started earlier this week" and (with
      // weekends hidden) "continues across the skipped weekend".
      const prevDay = dayIndex > 0 ? week[dayIndex - 1] : null;
      if (prevDay && this.#occupies(event, prevDay)) {
        continue;
      }
      // Span = consecutive visible columns the event covers from here, so a bar
      // crossing a hidden weekend spans Friday and the next Monday as adjacent.
      let span = 0;
      for (
        let i = dayIndex;
        i < week.length && this.#occupies(event, week[i]);
        i++
      ) {
        span++;
      }
      slots[lanes.get(event.bookingId) ?? 0] = { event, span };
    }
    return slots;
  }

  /**
   * Whether the column at `index` starts a new week relative to the previous
   * visible column (i.e. there is a calendar gap, as happens after Friday when
   * weekends are hidden). Used to render the week separator.
   */
  protected isNewWeek(index: number): boolean {
    const week = this.#week();
    if (index <= 0 || index >= week.length) {
      return false;
    }
    return differenceInCalendarDays(week[index], week[index - 1]) > 1;
  }

  /** Whether an event's day range covers the given day. */
  #occupies(event: CalendarEvent, day: Date): boolean {
    const offset = differenceInCalendarDays(day, event.calendarDateFrom);
    return offset >= 0 && offset < Math.max(1, event.eventDurationInDays);
  }

  /**
   * Lane assignment that keeps events in chronological order top-to-bottom.
   * Processing events by start (day, then time), each event is placed one lane
   * below the deepest event it overlaps (shares a day with) that was already
   * placed. Lanes are never reused above a still-running overlapping event, so a
   * later event always stacks beneath earlier ones it coincides with instead of
   * jumping into a gap left by a finished single-day event.
   */
  #assignLanes(events: CalendarEvent[]): Map<number, number> {
    const laneOf = new Map<number, number>();
    const placed: { bookingId: number; start: number; end: number }[] = [];
    const ranges = events
      .map(event => {
        const start = differenceInCalendarDays(
          event.calendarDateFrom,
          DAY_EPOCH
        );
        return {
          bookingId: event.bookingId,
          start,
          end: start + Math.max(1, event.eventDurationInDays) - 1,
          startTime: event.startTime.getTime(),
        };
      })
      .sort((a, b) =>
        a.start !== b.start ? a.start - b.start : a.startTime - b.startTime
      );
    for (const range of ranges) {
      let lane = 0;
      for (const other of placed) {
        const overlaps = range.start <= other.end && other.start <= range.end;
        if (overlaps) {
          lane = Math.max(lane, (laneOf.get(other.bookingId) ?? 0) + 1);
        }
      }
      laneOf.set(range.bookingId, lane);
      placed.push(range);
    }
    return laneOf;
  }

  protected onEventClick(event: CalendarEvent, roomDexId: string) {
    if (!this.canOpenEvent()) {
      return;
    }
    this.eventClick.emit({ event, roomDexId });
  }

  protected onRoomClick(room: CalendarRoomRow) {
    if (!this.canOpenRoom()) {
      return;
    }
    this.roomClick.emit(room);
  }

  protected onAddEvent(date: Date, roomDexId: string) {
    this.addEvent.emit({ date, roomDexId });
  }

  /** Select a day cell to reveal its "add event" button (touch fallback). */
  protected selectCell(roomDexId: string, day: Date) {
    if (!this.canAddEvent()) {
      return;
    }
    this.selectedCell.set({ roomDexId, time: day.getTime() });
  }

  protected isCellSelected(roomDexId: string, day: Date): boolean {
    const selected = this.selectedCell();
    return (
      selected?.roomDexId === roomDexId && selected?.time === day.getTime()
    );
  }

  protected onSortChange() {
    const newSort = this.sortDirection() === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit(newSort);
  }

  #setWeek(date: Date) {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    if (!this.hideWeekends()) {
      this.#week.set(
        eachDayOfInterval({
          start,
          end: endOfWeek(date, { weekStartsOn: 1 }),
        })
      );
      return;
    }
    // Seven weekday columns: Mon–Fri, then Mon–Tue of the following week.
    const days: Date[] = [];
    let cursor = start;
    while (days.length < 7) {
      const dayOfWeek = cursor.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(cursor);
      }
      cursor = add(cursor, { days: 1 });
    }
    this.#week.set(days);
  }

  #emitWeekChange(userInitiated = false) {
    const week = this.#week();
    this.weekChange.emit({
      startDate: week[0],
      endDate: week[6],
      selectedDate: this.#selectedDay,
      userInitiated,
    });
  }
}
